import { Navigate } from '@tanstack/react-router';
import type { PropsWithChildren } from 'react';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';

/** The two Analysis URL segments. `analysis` is the legacy spelling. */
export type AnalysisSegment = 'analysis' | 'reports';

/** The pages served under either segment. The report is the segment itself. */
export type AnalysisPage = 'report' | 'deliverability' | 'email-clients';

const PAGE_ROUTES = {
  analysis: {
    report: '/workspaces/$workspaceId/analysis',
    deliverability: '/workspaces/$workspaceId/analysis/deliverability',
    'email-clients': '/workspaces/$workspaceId/analysis/email-clients',
  },
  reports: {
    report: '/workspaces/$workspaceId/reports',
    deliverability: '/workspaces/$workspaceId/reports/deliverability',
    'email-clients': '/workspaces/$workspaceId/reports/email-clients',
  },
} as const;

/** The segment the app's URLs use; `analysis` is the legacy spelling, kept live for bookmarks. */
export const CANONICAL_ANALYSIS_SEGMENT: AnalysisSegment = 'reports';

export function analysisPageRoute(segment: AnalysisSegment, page: AnalysisPage) {
  return PAGE_ROUTES[segment][page];
}

/**
 * True when `pathname` is this page under either segment.
 *
 * The report is the segment itself rather than a page beneath it, so it
 * matches a path that ends there: `/reports/deliverability` is not the report,
 * and a prefix test would say it was. Both the bare shape and the `journeys`
 * one count, since Hydra serves and forwards both.
 */
export function isAnalysisPagePathname(pathname: string, page: AnalysisPage) {
  const trimmed = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  if (page === 'report') {
    return trimmed.endsWith('/analysis') || trimmed.endsWith('/reports');
  }
  return trimmed.includes(`/analysis/${page}`) || trimmed.includes(`/reports/${page}`);
}

/**
 * Renders one of the three Analysis pages at the canonical segment, and forwards the
 * legacy segment to it.
 *
 * Sits above the plan guard so a non-canonical URL is corrected before it runs. The two
 * tab routes also place it above the `last` workspace resolver, forwarding the sentinel
 * untouched for the canonical route to resolve; the report cannot, because its rollout
 * gate builds the flag-off hand-back from the pathname and has nothing to build from
 * while the workspace is still `last`. There the sentinel resolves first and this
 * forward is the second hop.
 */
type AnalysisRouteViewProps = PropsWithChildren<{
  segment: AnalysisSegment;
  page: AnalysisPage;
  workspaceId: WorkspaceIdRouteParamInput;
}>;

export function AnalysisRouteView({ segment, page, workspaceId: workspaceIdParam, children }: AnalysisRouteViewProps) {
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  if (segment !== CANONICAL_ANALYSIS_SEGMENT) {
    // Only the segment changes. The query (the shared date range) and the hash cross the
    // redirect as they are, which is what the page being ported's own redirect does.
    return (
      <Navigate to={analysisPageRoute(CANONICAL_ANALYSIS_SEGMENT, page)} params={{ workspaceId }} search hash replace />
    );
  }

  return children;
}
